import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Divider,
  Tooltip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

export function DataQualityIndicator({ onOpenModal }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        Data Imbalances:
      </Typography>
      <Tooltip title="75% of studies used R-22 refrigerant" arrow>
        <Chip label="R-22: 75%" size="small" variant="outlined" color="warning" sx={{ cursor: 'help' }} />
      </Tooltip>
      <Tooltip title="39% of studies from New York and Florida" arrow>
        <Chip label="NY/FL: 39%" size="small" variant="outlined" color="warning" sx={{ cursor: 'help' }} />
      </Tooltip>
      <Tooltip title="49% of equipment studied were chillers" arrow>
        <Chip label="Chillers: 49%" size="small" variant="outlined" color="warning" sx={{ cursor: 'help' }} />
      </Tooltip>
      <IconButton size="small" onClick={onOpenModal} color="info">
        <InfoIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

export function DataQualityModal({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Data Quality: Class Imbalances</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The corpus data has known class imbalances. Results are most reliable for well-represented categories.
        </Typography>

        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>
                <Chip label="R-22: 75%" size="small" color="warning" />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  75% of studies used <strong>R-22 refrigerant</strong>. Newer refrigerants like R-410A and R-134a are underrepresented in the data.
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                <Chip label="NY/FL: 39%" size="small" color="warning" />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  39% of studies are from <strong>New York and Florida</strong>. Other states and regions have less data coverage.
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                <Chip label="Chillers: 49%" size="small" color="warning" />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  49% of equipment studied were <strong>chillers</strong>. RTUs, split systems, and other equipment types have fewer data points.
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        <Alert severity="info" icon={false}>
          <Typography variant="subtitle2" gutterBottom>
            Why This Matters
          </Typography>
          <Typography variant="body2">
            Results are most reliable for <strong>R-22 chillers in NY/FL</strong>.
            Extrapolating to R-410A equipment in other regions has higher uncertainty.
            The confidence intervals (like "±13.13%") account for this, but users should
            be aware when interpreting results for underrepresented categories.
          </Typography>
        </Alert>
      </DialogContent>
    </Dialog>
  )
}

// Combined component with state management
export default function DataQualityInfo() {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <>
      <DataQualityIndicator onOpenModal={() => setModalOpen(true)} />
      <DataQualityModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}
